'use client'

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchProfilePictureBlob } from '@/services/profile'
import { useAuth } from '@/providers/AuthContext'

/**
 * Foto do número WhatsApp do tenant (rail `BrandMenu` e tela admin do número — Task 7 invalida a mesma
 * queryKey após upload pra atualizar os dois juntos) como object URL.
 * 404 = sem foto, estado esperado (não reintentado) → `isError` true, caller cai no fallback.
 */
export function useProfilePicture() {
  const { user } = useAuth()
  const tenantId = user?.tenant_id

  const { data: blob, isError } = useQuery({
    // Escopada por tenant (Task 9, bug A): sem o sufixo, trocar de tenant (login em outra empresa)
    // reaproveitava o blob em cache do tenant anterior até o usuário limpar dados manualmente.
    // `useAdminPhones.uploadPicture` invalida com `['profile','picture']` (sem sufixo) — TanStack Query
    // v5 casa por PREFIXO por padrão, então a invalidação continua batendo aqui.
    queryKey: ['profile', 'picture', tenantId],
    // Passa o tenantId como cache-bust (`?t=`) — o queryKey só isola o cache do TanStack (memória); a URL
    // da imagem é idêntica pra todos os tenants, então SEM isso o cache HTTP do browser serve a foto do
    // primeiro tenant pra todos (bug reportado). Ver `fetchProfilePictureBlob`.
    queryFn: () => fetchProfilePictureBlob(tenantId),
    // Sem tenantId ainda (auth carregando) não busca — evita reaproveitar/exibir foto sob uma key
    // "genérica" antes do tenant ser conhecido.
    enabled: tenantId !== undefined,
    staleTime: Infinity,
    retry: false,
  })

  const [src, setSrc] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!blob) {
      setSrc(undefined)
      return
    }

    const objectUrl = URL.createObjectURL(blob)
    setSrc(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [blob])

  return { src, isError }
}
